import { Environment, Metadata, Plan } from '@workspaces/types'
import BrowserEnvironmentResolver from './environment.helper'
import PlanService from '@/services/plan.service'
import { usePlanStore } from '@/store/plan'
import {
  getRealtimeReactionForPlans,
  isCommentsFunctionalityEnabled,
  isPackagagesFeatureEnabled,
  isPlansRealtimeFeatureEnabled,
} from '@/plan/metadata/metadata.helper'
import { subscribeToPlans as subscribeToPlansService } from '@workspaces/services'
import { getAppMetadata } from '@/plan/metadata/metadata'
import { getInstance as getAuth0ServiceInstance } from '@/auth/index'
import {
  areQuickAccessCommentsInitialized,
  updateQuickAcessPlansComments,
} from './comment.helper'

function setClientNameForPlans(
  meta: Metadata.AppMetadata,
  plans: Plan.Plan[],
  clients: Record<string, string>,
): Plan.Plan[] {
  if (!isPackagagesFeatureEnabled(meta)) {
    return plans
  }
  return plans.map((plan) => {
    const clientName = plan.client ? clients[plan.client] : ''
    return {
      ...plan,
      clientName,
    }
  })
}

async function fetchMyPlans(
  userId: string,
  meta: Metadata.AppMetadata,
  environment: Environment.EnvironmentResolver,
): Promise<void> {
  try {
    const clients = usePlanStore().getPlansClients
    const plans = await PlanService.getPlansByUserId(meta, environment, userId)
    let plansFormatted: Plan.Plan[] = plans.map((p: Plan.Plan) => {
      return {
        ...p,
        modified: p.modified_at?.toISOString(),
        user: {
          id: p.created_by,
          name: p.created_by.split('@')[0],
        },
      }
    })
    plansFormatted = setClientNameForPlans(meta, plansFormatted, clients)
    usePlanStore().setMyPlans(plansFormatted)
    console.debug('📒 My plans: ', plansFormatted.length)
  } catch (error) {
    usePlanStore().setMyPlans([])
  }
}

async function fetchPublicPlans(
  meta: Metadata.AppMetadata,
  environment: Environment.EnvironmentResolver,
): Promise<void> {
  try {
    const clients = usePlanStore().getPlansClients
    const plans = await PlanService.getPublicPlans(meta, environment)
    let plansFormatted: Plan.Plan[] = plans.map((p: Plan.Plan) => {
      return {
        ...p,
        modified: p.modified_at?.toISOString(),
        user: {
          id: p.created_by,
          name: p.created_by.split('@')[0],
        },
      }
    })
    plansFormatted = setClientNameForPlans(meta, plansFormatted, clients)
    usePlanStore().setPublicPlans(plansFormatted)
    console.debug('📒 Public plans: ', plansFormatted.length)
  } catch (error) {
    usePlanStore().setPublicPlans([])
  }
}

async function fetchPlans(
  userId: string,
  meta: Metadata.AppMetadata,
  environment: Environment.EnvironmentResolver,
): Promise<void> {
  await Promise.all([
    fetchMyPlans(userId, meta, environment),
    fetchPublicPlans(meta, environment),
  ])
}

export async function fetchPlansVisibleByUser(): Promise<void> {
  console.debug('📒 Fetching plans visible by user')
  const userId = getAuth0ServiceInstance().getUser()
  const meta = getAppMetadata()
  const environment = BrowserEnvironmentResolver.getInstance()
  await fetchPlans(userId, meta, environment)
  // The comments subscription owns ongoing comment refreshes. We only kick one
  // off from here for the initial population, where we must guarantee plans are
  // loaded first; afterwards a plan-list change no longer re-scans comments.
  if (
    isCommentsFunctionalityEnabled(meta) &&
    !areQuickAccessCommentsInitialized()
  ) {
    console.debug('📒 Comments are enabled, performing initial population')
    await updateQuickAcessPlansComments()
  }
}

export function getFetchedPlansForComments(): Plan.PlanForCommentFetching[] {
  const userPlans = usePlanStore().getMyPlans
  const publicPlans = usePlanStore().getPublicPlans
  const uniquePlans: Plan.PlanForCommentFetching[] = []
  const plans = [...userPlans, ...publicPlans]
  plans.forEach((plan) => {
    const planForCommentFetching: Plan.PlanForCommentFetching = {
      planId: plan.id,
      planName: plan.name,
      createdBy: plan.created_by,
    }
    if (!uniquePlans.some((p) => p.planId === plan.id)) {
      uniquePlans.push(planForCommentFetching)
    }
  })

  return uniquePlans
}

export async function subscribeToPlans(
  meta: Metadata.AppMetadata,
  environment: Environment.EnvironmentResolver,
): Promise<void> {
  if (!isPlansRealtimeFeatureEnabled(meta)) {
    return
  }
  const isSubscribed = usePlanStore().getIsSubscribedToPlans
  if (isSubscribed) {
    console.warn(
      'Already subscribed to plans. This should be not happening, please check how this is being called',
    )
    console.debug('📒 ✋ Already subscribed to plans. Nothing to do')
    return
  }
  console.debug('📒📒📒📒 Subscribing to plans 📒📒📒📒')
  const debounceDelay = getRealtimeReactionForPlans(meta)
  console.debug('📒 Debounce delay for plans in seconds: ', debounceDelay)
  const unscribeFunction = await subscribeToPlansService(
    meta,
    environment,
    debounceDelay,
    fetchPlansVisibleByUser,
  )
  window.addEventListener('beforeunload', () => {
    if (unscribeFunction) {
      console.debug('📒 ✅ Unsubscribing from plans before closing')
      unscribeFunction()
      usePlanStore().setIsSubscribedToPlans(false)
    }
  })
  usePlanStore().setIsSubscribedToPlans(true)
}
